import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Image, Star, Trophy, MessageSquare, Bot, Eye, Trash2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface Stats {
  gallery: number;
  testimonials: number;
  wallOfFame: number;
  messages: number;
  unreadMessages: number;
  chatbotLeads: number;
}

interface DayData {
  date: string;
  views: number;
  unique: number;
}

interface GalleryItem {
  id: string;
  title: string;
  image_url: string;
  created_at: string;
}

interface Testimonial {
  id: string;
  name: string;
  quote: string;
  created_at: string;
}

interface Video {
  id: string;
  title: string;
  video_url: string;
  created_at: string;
}

const Dashboard = () => {
  const [stats, setStats] = useState<Stats>({ gallery: 0, testimonials: 0, wallOfFame: 0, messages: 0, unreadMessages: 0, chatbotLeads: 0 });
  const [chartData, setChartData] = useState<DayData[]>([]);
  const [totalViews, setTotalViews] = useState(0);
  const [uniqueSessions, setUniqueSessions] = useState(0);
  const [topPages, setTopPages] = useState<{ page: string; count: number }[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);

  const fetchStats = useCallback(async () => {
    const [g, t, w, m, u, cl] = await Promise.all([
      supabase.from('gallery_items').select('id', { count: 'exact', head: true }),
      supabase.from('testimonials').select('id', { count: 'exact', head: true }),
      supabase.from('wall_of_fame').select('id', { count: 'exact', head: true }),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }).eq('is_read', false),
      supabase.from('chatbot_leads').select('id', { count: 'exact', head: true }),
    ]);
    setStats({
      gallery: g.count ?? 0,
      testimonials: t.count ?? 0,
      wallOfFame: w.count ?? 0,
      messages: m.count ?? 0,
      unreadMessages: u.count ?? 0,
      chatbotLeads: cl.count ?? 0,
    });
  }, []);

  const fetchContent = useCallback(async () => {
    const [gRes, tRes, vRes] = await Promise.all([
      supabase.from('gallery_items').select('id, title, image_url, created_at').order('created_at', { ascending: false }).limit(10),
      supabase.from('testimonials').select('id, name, quote, created_at').order('created_at', { ascending: false }).limit(10),
      supabase.from('testimonial_videos').select('id, title, video_url, created_at').order('created_at', { ascending: false }).limit(10),
    ]);
    setGalleryItems(gRes.data ?? []);
    setTestimonials(tRes.data ?? []);
    setVideos(vRes.data ?? []);
  }, []);

  const extractStoragePath = (url: string, bucket: string) => {
    const marker = `/storage/v1/object/public/${bucket}/`;
    const idx = url.indexOf(marker);
    if (idx !== -1) return url.substring(idx + marker.length);
    return null;
  };

  const deleteGalleryItem = async (item: GalleryItem) => {
    const path = extractStoragePath(item.image_url, 'gallery');
    if (path) {
      await supabase.storage.from('gallery').remove([path]);
    }
    const { error } = await supabase.from('gallery_items').delete().eq('id', item.id);
    if (error) { toast.error('Chyba při mazání'); return; }
    toast.success('Fotka smazána');
    fetchStats();
    fetchContent();
  };

  const deleteTestimonial = async (item: Testimonial) => {
    const { error } = await supabase.from('testimonials').delete().eq('id', item.id);
    if (error) { toast.error('Chyba při mazání'); return; }
    toast.success('Reference smazána');
    fetchStats();
    fetchContent();
  };

  const deleteVideo = async (item: Video) => {
    const path = extractStoragePath(item.video_url, 'testimonial-videos');
    if (path) {
      await supabase.storage.from('testimonial-videos').remove([path]);
    }
    const { error } = await supabase.from('testimonial_videos').delete().eq('id', item.id);
    if (error) { toast.error('Chyba při mazání'); return; }
    toast.success('Video smazáno');
    fetchStats();
    fetchContent();
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data } = await supabase
        .from('page_views')
        .select('created_at, session_id, page_url')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at');

      if (!data || data.length === 0) { setChartData([]); return; }

      setTotalViews(data.length);
      setUniqueSessions(new Set(data.map(d => d.session_id)).size);

      const dayMap: Record<string, { views: number; sessions: Set<string> }> = {};
      data.forEach((row) => {
        const day = row.created_at.slice(0, 10);
        if (!dayMap[day]) dayMap[day] = { views: 0, sessions: new Set() };
        dayMap[day].views++;
        if (row.session_id) dayMap[day].sessions.add(row.session_id);
      });

      setChartData(Object.entries(dayMap).sort(([a], [b]) => a.localeCompare(b)).map(([date, val]) => ({
        date: date.slice(5),
        views: val.views,
        unique: val.sessions.size,
      })));

      const pageMap: Record<string, number> = {};
      data.forEach((row) => { pageMap[row.page_url] = (pageMap[row.page_url] || 0) + 1; });
      setTopPages(Object.entries(pageMap).sort(([, a], [, b]) => b - a).slice(0, 5).map(([page, count]) => ({ page, count })));
    };

    fetchStats();
    fetchContent();
    fetchAnalytics();
  }, [fetchStats, fetchContent]);

  const cards = [
    { label: 'Fotky v galerii', value: stats.gallery, icon: Image },
    { label: 'Reference', value: stats.testimonials, icon: Star },
    { label: 'Wall of Fame', value: stats.wallOfFame, icon: Trophy },
    { label: 'Zprávy', value: `${stats.unreadMessages} nových / ${stats.messages}`, icon: MessageSquare },
    { label: 'Chatbot leady', value: stats.chatbotLeads, icon: Bot },
  ];

  const DeleteButton = ({ onConfirm, label }: { onConfirm: () => void; label: string }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0">
          <Trash2 size={16} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Smazat položku?</AlertDialogTitle>
          <AlertDialogDescription>Opravdu chcete smazat „{label}"? Tuto akci nelze vrátit.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Zrušit</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Smazat</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <div>
      <h1 className="text-4xl tracking-wider text-foreground mb-8" style={{ fontFamily: 'Bebas Neue' }}>
        Dashboard
      </h1>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((card) => (
          <div key={card.label} className="p-5 bg-card border border-border rounded-md">
            <card.icon className="text-primary mb-2" size={22} />
            <p className="text-2xl font-bold text-foreground">{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 p-5 bg-card border border-border rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Eye size={18} className="text-primary" /> Návštěvnost (30 dní)
            </h2>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>Celkem: <strong className="text-foreground">{totalViews}</strong></span>
              <span>Unikátní: <strong className="text-foreground">{uniqueSessions}</strong></span>
            </div>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '6px', color: 'hsl(var(--foreground))' }} />
                <Bar dataKey="views" name="Zobrazení" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="unique" name="Unikátní" fill="hsl(var(--primary) / 0.4)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-16">Zatím žádná data o návštěvnosti.</p>
          )}
        </div>

        <div className="p-5 bg-card border border-border rounded-md">
          <h2 className="text-lg font-semibold text-foreground mb-4">Top stránky</h2>
          {topPages.length > 0 ? (
            <div className="space-y-3">
              {topPages.map((p, i) => (
                <div key={p.page} className="flex items-center justify-between">
                  <span className="text-sm text-foreground truncate">
                    <span className="text-muted-foreground mr-2">{i + 1}.</span>{p.page}
                  </span>
                  <span className="text-sm font-medium text-primary">{p.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Zatím žádná data.</p>
          )}
        </div>
      </div>

      {/* Content Management Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gallery */}
        <div className="p-5 bg-card border border-border rounded-md">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Image size={18} className="text-primary" /> Poslední fotky
          </h2>
          {galleryItems.length > 0 ? (
            <div className="space-y-3">
              {galleryItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <img src={item.image_url} alt={item.title} className="w-12 h-12 rounded object-cover shrink-0" />
                  <span className="text-sm text-foreground truncate flex-1">{item.title || 'Bez názvu'}</span>
                  <DeleteButton onConfirm={() => deleteGalleryItem(item)} label={item.title || 'fotku'} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Žádné fotky.</p>
          )}
        </div>

        {/* Testimonials */}
        <div className="p-5 bg-card border border-border rounded-md">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Star size={18} className="text-primary" /> Poslední reference
          </h2>
          {testimonials.length > 0 ? (
            <div className="space-y-3">
              {testimonials.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.quote}</p>
                  </div>
                  <DeleteButton onConfirm={() => deleteTestimonial(item)} label={item.name} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Žádné reference.</p>
          )}
        </div>

        {/* Videos */}
        <div className="p-5 bg-card border border-border rounded-md">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Trophy size={18} className="text-primary" /> Poslední videa
          </h2>
          {videos.length > 0 ? (
            <div className="space-y-3">
              {videos.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <video src={item.video_url} className="w-16 h-12 rounded object-cover shrink-0" muted preload="metadata" />
                  <span className="text-sm text-foreground truncate flex-1">{item.title || 'Bez názvu'}</span>
                  <DeleteButton onConfirm={() => deleteVideo(item)} label={item.title || 'video'} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Žádná videa.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
